// components/icons/AnalyzerIcons.tsx
import * as React from 'react';

export type IconProps = React.SVGProps<SVGSVGElement> & {
  /** Tailwind classes for size or any extra classes, e.g. "w-5 h-5 text-primary" */
  className?: string;
  /** Numeric or string size; if provided, sets width/height attributes (e.g. 18 or "20px") */
  size?: number | string;
  /** Stroke width (default 1.8 for compact crispness) */
  strokeWidth?: number;
};

function SvgBase({
  children,
  className,
  size,
  strokeWidth = 1.8,
  ...rest
}: React.PropsWithChildren<IconProps>) {
  const w = size ?? 18;
  const h = size ?? 18;
  return (
    <svg
      viewBox="0 0 24 24"
      width={w}
      height={h}
      className={className ?? 'w-[18px] h-[18px]'}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* =========================
   Common analyzer icons
   ========================= */

export function IconBalance(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="m7 7-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Z" />
      <path d="m17 7-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Z" />
    </SvgBase>
  );
}

export function IconCompare(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M10 3H5a2 2 0 0 0-2 2v5" />
      <path d="M20 14v5a2 2 0 0 1-2 2h-5" />
      <path d="m3 21 6-6" />
      <path d="m21 3-6 6" />
    </SvgBase>
  );
}

export function IconHelp(props: IconProps) {
  // default smaller usage often looks better
  const { className = 'w-4 h-4', ...rest } = props;
  return (
    <SvgBase className={className} {...rest}>
      <path d="M12 18h.01" />
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 6a2 2 0 0 1 2 2c0 1.5-2 2-2 3" />
    </SvgBase>
  );
}

export function IconSwap(props: IconProps) {
  return (
    <SvgBase {...props}>
      <polyline points="8 17 3 12 8 7" />
      <polyline points="16 7 21 12 16 17" />
    </SvgBase>
  );
}

export function IconScan(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
    </SvgBase>
  );
}

export function IconRefresh(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M21 12a9 9 0 1 1-3.51-7.07" />
      <path d="M21 3v6h-6" />
    </SvgBase>
  );
}

/** Clean, crisp gear (replaces the blobby gear). Scales well at 16–24px. */
export function IconGear(props: IconProps) {
  const { strokeWidth = 1.8, ...rest } = props;
  return (
    <SvgBase strokeWidth={strokeWidth} {...rest}>
      {/* Outer ring + inner hub */}
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.5" />

      {/* Teeth (top tooth, then rotated copies around the center) */}
      <rect x="11" y="1.5" width="2" height="4" rx="1" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(45 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(90 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(135 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(180 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(225 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(270 12 12)" />
      <rect x="11" y="1.5" width="2" height="4" rx="1" transform="rotate(315 12 12)" />
    </SvgBase>
  );
}

export function IconLink(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 1 0-7.07-7.07l-1.2 1.2" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 1 0 7.07 7.07l1.2-1.2" />
    </SvgBase>
  );
}

export function IconSpark(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M4.93 4.93 7.76 7.76" />
      <path d="M16.24 16.24l2.83 2.83" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="M4.93 19.07 7.76 16.24" />
      <path d="M16.24 7.76l2.83-2.83" />
    </SvgBase>
  );
}

export function IconBarcode(props: IconProps) {
  return (
    <SvgBase {...props}>
      <path d="M3 5v14" />
      <path d="M6 5v14" />
      <path d="M9 5v14" />
      <path d="M11 5v14" />
      <path d="M14 5v14" />
      <path d="M17 5v14" />
      <path d="M21 5v14" />
    </SvgBase>
  );
}

/* =========================
   Convenience re-exports
   ========================= */
// You can also export a default object if you like using object import style.
// export default { IconBalance, IconCompare, IconHelp, IconSwap, IconScan, IconRefresh, IconGear, IconLink, IconSpark, IconBarcode };
